import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntdLayout,
  Menu,
  Avatar,
  Dropdown,
  Button,
  message,
  Space,
  Typography,
  Badge,
  Drawer,
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  DollarOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  BookOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { getUserInfo, clearAuthData, formatUserDisplayName, hasRole, UserInfo } from '../../utils/auth';
import { logout } from '../../services/auth';
import { UserRole, isAdminRole } from '../../constants/user';
import { getUpcomingReminders } from '../../services/followUp';
import { useResponsive } from '../../hooks/useResponsive';
import MobileBottomNav from '../MobileBottomNav';

const { Header, Sider, Content } = AntdLayout;
const { Text } = Typography;

const Layout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(getUserInfo());
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = userInfo?.role === UserRole.ADMIN;
  const { isMobile } = useResponsive();

  // Refresh user info from server
  const refreshUserInfo = async () => {
    try {
      const { getCurrentUser } = await import('../../services/auth');
      const { setUserInfo: saveUserInfo } = await import('../../utils/auth');
      const freshUserInfo = await getCurrentUser();
      saveUserInfo(freshUserInfo);
      setUserInfo(freshUserInfo);
    } catch (error) {
      console.error('Failed to refresh user info:', error);
      // 如果获取失败，使用本地缓存的信息
    }
  };

  // Load reminder count
  const loadReminderCount = async () => {
    try {
      const reminders = await getUpcomingReminders({
        days_ahead: 1, // 获取今天和明天的提醒
        page: 1,
        page_size: 100 // 足够大的数量
      });
      setReminderCount(reminders.length);
    } catch (error) {
      console.error('Failed to load reminder count:', error);
      // 静默失败，不影响用户体验
    }
  };

  // Refresh user info and load reminder count on mount
  useEffect(() => {
    refreshUserInfo();
    loadReminderCount();

    // 每5分钟刷新一次提醒数量
    const interval = setInterval(loadReminderCount, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Menu items based on user role
  const menuItems = [];

  // 财务人员只能看到财务管理
  if (userInfo?.role === UserRole.FINANCE) {
    menuItems.push({
      key: '/finance',
      icon: <DollarOutlined />,
      label: '财务管理',
    });
  } else {
    // 普通员工和管理员可以看到业务模块
    menuItems.push(
      {
        key: '/workspace',
        icon: <BookOutlined />,
        label: '工作台',
      },
      {
        key: '/leads',
        icon: <UserOutlined />,
        label: '线索管理',
      },
      {
        key: '/cases',
        icon: <FileTextOutlined />,
        label: '案件管理',
      }
    );

    // 管理员额外可以看到财务管理、数据看板和配置中心
    if (isAdmin) {
      menuItems.push(
        {
          key: '/finance',
          icon: <DollarOutlined />,
          label: '财务管理',
        },
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: '数据看板',
        },
        {
          key: '/config',
          icon: <SettingOutlined />,
          label: '配置中心',
        }
      );
    }
  }

  // Handle menu click
  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      clearAuthData();
      message.success('退出登录成功');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout API fails, clear local data and redirect
      clearAuthData();
      navigate('/login');
    }
  };

  // User dropdown menu
  const userMenuItems: any[] = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: '个人资料',
    //   onClick: () => {
    //     message.info('个人资料功能开发中...');
    //   },
    // },
    // {
    //   key: 'settings',
    //   icon: <SettingOutlined />,
    //   label: '系统设置',
    //   onClick: () => {
    //     message.info('系统设置功能开发中...');
    //   },
    // },
    // {
    //   type: 'divider' as const,
    // },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <AntdLayout className="layout-container">
      {/* Header */}
      <Header className="layout-header" style={{ padding: isMobile ? '0 12px' : '0 24px' }}>
        {isMobile && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuVisible(true)}
            style={{ fontSize: '18px' }}
          />
        )}
        
        <div className="layout-logo">
          <BookOutlined />
          {!isMobile && <span>法律事务所CRM</span>}
        </div>

        <div className="layout-user-info">
          <Space size="middle">
            {/* User dropdown */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  src={userInfo?.avatar}
                />
                {!isMobile && <Text>{formatUserDisplayName(userInfo)}</Text>}
              </Space>
            </Dropdown>
          </Space>
        </div>
      </Header>

      <AntdLayout className="layout-body">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sider
            className="layout-sider"
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            width={200}
            collapsedWidth={60}
          >
            {/* Collapse toggle */}
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: '16px', width: 32, height: 32 }}
              />
            </div>

            {/* Navigation menu */}
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={handleMenuClick}
              style={{ borderRight: 0 }}
            />
          </Sider>
        )}

        {/* Mobile Menu Drawer */}
        {isMobile && (
          <Drawer
            title="菜单"
            placement="left"
            onClose={() => setMobileMenuVisible(false)}
            open={mobileMenuVisible}
            width={250}
          >
            <Menu
              mode="inline"
              selectedKeys={[location.pathname]}
              items={menuItems}
              onClick={(e) => {
                handleMenuClick(e);
                setMobileMenuVisible(false);
              }}
              style={{ borderRight: 0 }}
            />
          </Drawer>
        )}

        {/* Main content */}
        <Content className={isMobile ? "layout-content mobile-layout-content" : "layout-content"}>
          <Outlet />
        </Content>
      </AntdLayout>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </AntdLayout>
  );
};

export default Layout;